import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { ArrowLeft, Search, Users, ShieldCheck, UserCheck, Loader2, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useConfirm } from '../components/ConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLES = ['', 'admin', 'agent', 'sales_agent', 'vendor', 'client'];

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { token, user: currentUser, setSession } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [skip, setSkip] = useState(0);
  const [impersonating, setImpersonating] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const limit = 25;

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
      if (q) params.set('q', q);
      if (role) params.set('role', role);
      const res = await axios.get(`${API}/admin/users?${params}`, { headers });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load users'); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, role, skip, token]);

  useEffect(() => {
    const t = setTimeout(load, q ? 350 : 0);  // light debounce on free-text search
    return () => clearTimeout(t);
  }, [load, q]);

  const signInAs = async (target) => {
    setImpersonating(target.user_id);
    try {
      const res = await axios.post(`${API}/admin/users/${target.user_id}/impersonate`, {}, { headers });
      // Stash the admin's own token so we can exit cleanly.
      localStorage.setItem('eventism_admin_backup_token', token);
      setSession(res.data.token, { ...res.data.user, _impersonator: { user_id: res.data.impersonator.user_id, email: res.data.impersonator.email } });
      toast.success(`Signed in as ${target.email}`);
      // Land them on the home dashboard (or vendor dashboard if vendor) so they
      // see what the user sees.
      if (target.role === 'vendor') window.location.href = '/vendor';
      else if (target.role === 'admin') window.location.href = '/admin/dashboard';
      else window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not impersonate');
      setImpersonating(null);
    }
  };

  const deleteUser = async (target) => {
    const counts = target._counts || {};
    const ok = await confirm({
      title: `Delete ${target.name || target.email}?`,
      body: `This permanently removes the user account, their vendor/provider profile, and CRM data. ${
        counts.products || counts.events || counts.bookings
          ? `Their ${[
              counts.products && `${counts.products} product${counts.products > 1 ? 's' : ''}`,
              counts.events && `${counts.events} event${counts.events > 1 ? 's' : ''}`,
              counts.bookings && `${counts.bookings} booking${counts.bookings > 1 ? 's' : ''}`,
            ].filter(Boolean).join(', ')} are kept for audit. `
          : ''
      }This cannot be undone.`,
      confirmText: 'Delete user',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    setDeleting(target.user_id);
    try {
      await axios.delete(`${API}/admin/users/${target.user_id}`, { headers });
      toast.success(`Deleted ${target.email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete user');
    } finally {
      setDeleting(null);
    }
  };

  const roleBadge = (r) => {
    const m = {
      admin: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
      agent: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
      sales_agent: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
      vendor: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
      client: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    };
    return <Badge variant="outline" className={`${m[r] || m.client} text-[10px]`}>{r || 'client'}</Badge>;
  };

  const page = Math.floor(skip / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="mb-2">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
            </Button>
            <h1 className="text-3xl font-bold flex items-center gap-2 font-['Space_Grotesk']">
              <Users className="w-7 h-7 text-fuchsia-400" /> User Directory
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Search any user, jump into their account to service it for them. All sessions are audit-logged.
            </p>
          </div>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setSkip(0); }}
                placeholder="Search by name, email, or business…"
                className="pl-9"
                data-testid="user-search-input"
              />
            </div>
            <Select value={role || 'all'} onValueChange={(v) => { setRole(v === 'all' ? '' : v); setSkip(0); }}>
              <SelectTrigger className="w-full sm:w-44" data-testid="user-role-filter">
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {ROLES.filter(Boolean).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : users.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">No users match.</div>
        ) : (
          <div className="space-y-2" data-testid="user-list">
            {users.map(u => (
              <Card key={u.user_id} className="hover:border-indigo-500/40 transition" data-testid={`user-row-${u.user_id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500/30 to-fuchsia-500/30 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {(u.name || u.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold truncate">{u.name || '(no name)'}</span>
                        {roleBadge(u.role)}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      {u.business_name && <div className="text-xs text-muted-foreground truncate">{u.business_name}</div>}
                      <div className="flex gap-3 mt-1 text-[11px] text-muted-foreground">
                        {u._counts?.products > 0 && <span>{u._counts.products} products</span>}
                        {u._counts?.events > 0 && <span>{u._counts.events} events</span>}
                        {u._counts?.bookings > 0 && <span>{u._counts.bookings} bookings</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => signInAs(u)}
                      disabled={impersonating === u.user_id || u.role === 'admin'}
                      className="bg-gradient-to-r from-indigo-600 to-fuchsia-600"
                      data-testid={`signin-as-${u.user_id}`}
                      title={u.role === 'admin' ? "Can't impersonate other admins" : 'Sign in as this user'}
                    >
                      {impersonating === u.user_id ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UserCheck className="w-4 h-4 mr-1.5" />}
                      {u.role === 'admin' ? 'Admin' : 'Sign in as'}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteUser(u)}
                      disabled={deleting === u.user_id || u.role === 'admin' || u.user_id === currentUser?.user_id}
                      data-testid={`delete-user-${u.user_id}`}
                      title={
                        u.role === 'admin' ? "Admins can't be deleted here"
                        : u.user_id === currentUser?.user_id ? "You can't delete your own account"
                        : 'Delete this user permanently'
                      }
                    >
                      {deleting === u.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-muted-foreground">Page {page} of {totalPages} · {total} users</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setSkip(Math.max(0, skip - limit))} disabled={skip === 0}>
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSkip(skip + limit)} disabled={skip + limit >= total}>
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="mt-8 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200 flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Security:</strong> Impersonation sessions expire after 2 hours and are recorded in the audit log
            (admin_user_id, target_user_id, IP, timestamp). Re-impersonation is blocked. You cannot impersonate other admins.
          </div>
        </div>
      </div>
    </div>
  );
}
